import React, { FC, useState } from 'react';
import { DeleteButton } from '../../../shared/globals';
import { T } from '../../../shared/helpers';
import { FormBuilderItem } from './index';
import styled, { css } from 'styled-components';
import update from 'immutability-helper';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { Checkbox } from '../checkbox';
import { FormControl } from '../formControl';
import { Icon } from '../icon';
import { Input } from '../input';
import { AdvancedSelect } from '../select';
import { TextArea } from '../textArea';

export interface Detail {
    pos?: number | undefined;
    value?: string | undefined;
    label?: string | undefined;
}

const EditFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
`;

export const LabelInput = styled(Input)`
    width: 100%;
    &:focus,
    &:active {
        border: ${(props) => props.theme.app.primary} 1px solid !important;
    }

    &:hover {
        border: 1px solid rgb(221, 221, 221);
    }
`;

export const ValueInput = styled(Input)`
    width: 100%;
    &:focus,
    &:active {
        border: ${(props) => props.theme.app.primary} 1px solid !important;
    }

    &:hover {
        border: 1px solid rgb(221, 221, 221);
    }
`;

const ItemContainer = styled.div<{ $xs: number }>`
    display: grid;
    grid-template-columns: 2fr 1fr 24px 24px;
    gap: 8px;
    align-items: start;

    ${Icon} {
        margin-top: 9px !important;
    }
`;

const AddOptionIcon = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid #dbe2e6;
    border-radius: 4px;
    background: #fff;
    font-size: 16px;
    font-weight: 600;
    color: #999;
    cursor: pointer;
    margin-top: 9px;
    flex-shrink: 0;

    &:hover {
        border-color: ${(props) => props.theme.app.primary};
        color: ${(props) => props.theme.app.primary};
    }

    ${Icon} {
        width: 12px;
        height: 12px;
        min-width: 12px;
        margin-top: 0 !important;
    }
`;

const EditContent = styled(TextArea)`
    width: 100% !important;
    height: 200px !important;
`;

const TwoColRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const TwoColRowWithCaption = styled.div``;

const SeparatedBlock = styled.div`
    margin-top: 14px;
`;

const HelperText = styled.div`
    color: #888;
    font-size: 12px;
    margin-top: 4px;
    line-height: 16px;
`;

const ErrorText = styled.div`
    color: #e53935;
    font-size: 12px;
    margin-top: 4px;
    line-height: 16px;
`;

const FieldBlock = styled.div<{ $hasError?: boolean }>`
    ${(p) =>
        p.$hasError &&
        css`
            input:not([type='checkbox']):not([type='radio']),
            textarea,
            select {
                border-color: #e53935 !important;
                background: #fff5f5 !important;
            }
        `}
`;

const EditForm: FC<{
    item: FormBuilderItem;
    invalidFields?: Set<string>;
    onChange: (result: FormBuilderItem) => void;
}> = (props) => {
    const [editableItem, setEditableItem] = useState(props.item || null);
    const [details, setDetails] = useState<Detail[]>((props.item.options as Detail[]) || ([{ pos: 1, value: '', label: '' }] as Detail[]));
    const [placeholder, setPlaceholder] = useState(props.item?.placeholder || '');
    const isEmailField = editableItem.type === 'email' && editableItem.name === 'field_email';

    const invalid = (field: string) => props.invalidFields?.has(field) ?? false;

    const isText = editableItem.typeField === 'text';
    const isNumber = editableItem.typeField === 'number';
    const isTextArea = editableItem.typeField === 'textArea';
    const isRadio = editableItem.typeField === 'inputRadio';
    const isCheckbox = editableItem.typeField === 'inputCheckbox';
    const isSelect = editableItem.typeField === 'select';
    const isHeader = editableItem.typeField === 'header';
    const isParagraph = editableItem.typeField === 'paragraph';

    const handleChange = (label: string, value: string | boolean) => {
        setEditableItem((prev) => {
            const getPreviuos = prev;

            if ((label == 'label' && getPreviuos.placeholder == '') || (label == 'label' && getPreviuos.placeholder === getPreviuos.label)) {
                prev = update(prev, {
                    ['placeholder']: { $set: value.toString() },
                    ['label']: { $set: value.toString() }
                });
                setPlaceholder((prev) => {
                    prev = value.toString();
                    return prev;
                });
            } else {
                if (label == 'placeholder') {
                    setPlaceholder((prev) => {
                        prev = value.toString();
                        return prev;
                    });
                }
                prev = update(prev, { [label]: { $set: value } });
            }
            props.onChange(prev);
            return prev;
        });
    };

    const handleChangeOption = (item: Detail, label: string, value: string) => {
        const next: Detail = { ...item };
        if (label == 'label') {
            next.label = value;
        } else {
            next.value = value;
        }

        const index = details?.findIndex((x) => x.pos == item.pos);

        if (index !== -1) {
            const options = update(details, { [index]: { $set: next } });
            setDetails(options);
            setEditableItem((prev) => {
                const updated =
                    options.length === 1 && label === 'value'
                        ? update(prev, { ['options']: { $set: options }, ['value']: { $set: value } })
                        : update(prev, { ['options']: { $set: options } });
                props.onChange(updated);
                return updated;
            });
        }
    };

    const handleRemoveOption = (item: Detail) => {
        const index = details?.findIndex((x) => x.pos == item.pos);

        if (index != undefined && index !== -1) {
            const options = details.filter((_, i) => i !== index);
            setDetails(options);
            setEditableItem((prev) => {
                const shouldResetDefault = editableItem.value === item.value;
                const updated = shouldResetDefault
                    ? update(prev, { ['options']: { $set: options }, ['value']: { $set: '' } })
                    : update(prev, { ['options']: { $set: options } });
                props.onChange(updated);
                return updated;
            });
        }
    };

    const handleAddField = () => {
        const item = details?.findLast((x) => x.pos);
        if (item) {
            const idx = item?.pos ? item?.pos + 1 : details.length + 1;
            const detail = { pos: idx, label: '', value: '' };
            const options = update(details, { $push: [detail] });
            setDetails(options);
            setEditableItem((prev) => {
                const updated = update(prev, { ['options']: { $set: options } });
                props.onChange(updated);
                return updated;
            });
        }
    };

    const handleDefaultOptionChange = (value: string) => {
        setEditableItem((prev) => {
            const updated = update(prev, { ['value']: { $set: value } });
            props.onChange(updated);
            return updated;
        });
    };

    const defaultOptionValue = typeof editableItem.value === 'string' ? editableItem.value : '';
    const hasLegacyDefault = defaultOptionValue !== '' && !details.some((d) => d.value === defaultOptionValue);

    return (
        <EditFormContainer>
            {/* Label + Required */}
            {!isHeader && !isParagraph && editableItem.hasOwnProperty('label') && (
                <FieldBlock $hasError={invalid('label')}>
                    <FormControl label={T._('Label', 'Admin')}>
                        <Input
                            id='label'
                            name='label'
                            type='text'
                            className='form-control'
                            defaultValue={editableItem.label}
                            onBlur={(e) => handleChange('label', e.currentTarget.value)}
                        />
                        {invalid('label') && <ErrorText>{T._('Label is required.', 'Admin')}</ErrorText>}
                        <Checkbox
                            disabled={isEmailField}
                            label={T._('Required', 'Admin')}
                            checked={editableItem.required || false}
                            onChange={(checked) => handleChange('required', checked)}
                        />
                    </FormControl>
                </FieldBlock>
            )}

            {/* Header text */}
            {isHeader && (
                <FieldBlock $hasError={invalid('headertext')}>
                    <FormControl label={T._('Header text', 'Admin')}>
                        <Input
                            id='headertext'
                            name='headertext'
                            type='text'
                            className='form-control'
                            defaultValue={editableItem.headertext}
                            onBlur={(e) => handleChange('headertext', e.currentTarget.value)}
                        />
                    </FormControl>
                    {invalid('headertext') && <ErrorText>{T._('Header text is required.', 'Admin')}</ErrorText>}
                </FieldBlock>
            )}

            {/* Header level */}
            {isHeader && (
                <FormControl label={T._('Header level', 'Admin')}>
                    <AdvancedSelect
                        key={'headerlevel'}
                        name={'headerlevel'}
                        value={editableItem.headerlevel ?? 'h1'}
                        className='form-control'
                        onChange={(e) => handleChange('headerlevel', e)}
                        onBlur={(e) => handleChange('headerlevel', e)}
                    >
                        <option value='h1'>{T._('H1', 'Admin')}</option>
                        <option value='h2'>{T._('H2', 'Admin')}</option>
                        <option value='h3'>{T._('H3', 'Admin')}</option>
                        <option value='h4'>{T._('H4', 'Admin')}</option>
                        <option value='h5'>{T._('H5', 'Admin')}</option>
                        <option value='h6'>{T._('H6', 'Admin')}</option>
                    </AdvancedSelect>
                    <HelperText>{T._('H1 is the largest, H6 the smallest.', 'Admin')}</HelperText>
                </FormControl>
            )}

            {/* Paragraph content */}
            {isParagraph && (
                <FieldBlock $hasError={invalid('content')}>
                    <FormControl label={T._('Content', 'Admin')}>
                        <EditContent
                            id='content'
                            name='content'
                            className='form-control'
                            defaultValue={editableItem.content}
                            onBlur={(e) => handleChange('content', e.currentTarget.value)}
                        />
                        {invalid('content') && <ErrorText>{T._('Content is required.', 'Admin')}</ErrorText>}
                        <HelperText>
                            {T._('Static text shown to customers in the form (e.g. instructions or notes). Not editable by customers.', 'Admin')}
                        </HelperText>
                    </FormControl>
                </FieldBlock>
            )}

            {/* Options (radio / select) */}
            {(isRadio || isSelect) && editableItem?.options != undefined && editableItem?.options?.length > 0 && (
                <FormControl label={T._('Options', 'Admin')}>
                    <HelperText>{T._('Label is shown to users; Value is the answer that gets stored.', 'Admin')}</HelperText>
                    <div>
                        {details?.map((x, idx) => (
                            <ItemContainer key={x.pos} $xs={3}>
                                <FieldBlock $hasError={invalid(`options[${idx}].label`)}>
                                    <LabelInput
                                        readOnly={editableItem?.readOnly}
                                        id={'props_element_' + x.pos + '_label'}
                                        type='text'
                                        name='label'
                                        placeholder={T._('Label', 'Admin')}
                                        className='form-control'
                                        defaultValue={x.label || ''}
                                        onBlur={(e) => handleChangeOption(x, 'label', e.currentTarget.value)}
                                    />
                                    {invalid(`options[${idx}].label`) && <ErrorText>{T._('Option label is required.', 'Admin')}</ErrorText>}
                                </FieldBlock>
                                <FieldBlock $hasError={invalid(`options[${idx}].value`)}>
                                    <ValueInput
                                        readOnly={false}
                                        id={'props_element_' + x.pos + '_value'}
                                        name='value'
                                        placeholder={T._('Value', 'Admin')}
                                        className='form-control'
                                        defaultValue={x.value || ''}
                                        onBlur={(e) => handleChangeOption(x, 'value', e.currentTarget.value)}
                                    />
                                    {invalid(`options[${idx}].value`) && <ErrorText>{T._('Option value is required.', 'Admin')}</ErrorText>}
                                </FieldBlock>
                                {x?.pos && x?.pos > 1 && !editableItem?.readOnly ? <DeleteButton onClick={() => handleRemoveOption(x)} /> : <div />}
                                {!editableItem.readOnly && idx === details.length - 1 ? (
                                    <AddOptionIcon onClick={() => handleAddField()}>
                                        <Icon>
                                            <PlusIcon />
                                        </Icon>
                                    </AddOptionIcon>
                                ) : (
                                    <div />
                                )}
                            </ItemContainer>
                        ))}
                    </div>
                </FormControl>
            )}

            {/* Default option (radio / select) */}
            {(isRadio || isSelect) && (
                <FormControl label={T._('Default option', 'Admin')}>
                    <AdvancedSelect
                        key={'default-option'}
                        name={'default-option'}
                        value={defaultOptionValue}
                        className='form-control'
                        onChange={(e) => handleDefaultOptionChange(e)}
                        onBlur={(e) => handleDefaultOptionChange(e)}
                    >
                        <option value=''>{T._('— None —', 'Admin')}</option>
                        {details
                            .filter((d) => d.value)
                            .map((d) => (
                                <option key={d.value} value={d.value}>
                                    {d.label || T._('(no label)', 'Admin')}
                                </option>
                            ))}
                        {hasLegacyDefault && <option value={defaultOptionValue}>{`${defaultOptionValue} (legacy)`}</option>}
                    </AdvancedSelect>
                    <HelperText>{T._('Option pre-selected when the form opens. Customers can still change it.', 'Admin')}</HelperText>
                </FormControl>
            )}

            {/* Type selector (text fields) */}
            {!isEmailField && isText && editableItem.hasOwnProperty('type') && (
                <FormControl label={T._('Type', 'Admin')}>
                    <AdvancedSelect
                        key={'type'}
                        name={'type'}
                        className='form-control'
                        value={editableItem.type}
                        onChange={(e) => handleChange('type', e)}
                        onBlur={(e) => handleChange('type', e)}
                    >
                        <option value='text'>{T._('text', 'Admin')}</option>
                        <option value='email'>{T._('email', 'Admin')}</option>
                        <option value='password'>{T._('password', 'Admin')}</option>
                        <option value='tel'>{T._('tel', 'Admin')}</option>
                        <option value='url'>{T._('url', 'Admin')}</option>
                        <option value='date'>{T._('date', 'Admin')}</option>
                    </AdvancedSelect>
                </FormControl>
            )}

            {/* Placeholder (text / number / textArea only) */}
            {(isText || isNumber || isTextArea) && editableItem.hasOwnProperty('placeholder') && (
                <FormControl label={T._('Placeholder', 'Admin')}>
                    <Input
                        id='placeholder'
                        name='placeholder'
                        type='text'
                        className='form-control'
                        defaultValue={placeholder}
                        value={placeholder}
                        onChange={(e) => setPlaceholder(e.currentTarget.value)}
                        onBlur={(e) => handleChange('placeholder', e.currentTarget.value)}
                    />
                </FormControl>
            )}

            {/* Default value (text / number / textArea) */}
            {!isEmailField && (isText || isNumber || isTextArea) && editableItem.hasOwnProperty('value') && typeof editableItem.value === 'string' && (
                <FormControl label={T._('Default value', 'Admin')}>
                    <Input
                        id='value'
                        name='value'
                        type='text'
                        className='form-control'
                        defaultValue={editableItem?.value?.toString() ?? ''}
                        onBlur={(e) => handleChange('value', e.currentTarget.value)}
                    />
                    <HelperText>{T._('Pre-filled value shown when the form opens.', 'Admin')}</HelperText>
                </FormControl>
            )}

            {/* Default selected (checkbox) */}
            {isCheckbox && editableItem.hasOwnProperty('defaultSelected') && (
                <SeparatedBlock>
                    <FormControl>
                        <Checkbox
                            label={T._('Default selected', 'Admin')}
                            checked={editableItem.defaultSelected || false}
                            onChange={(checked) => handleChange('defaultSelected', checked)}
                        />
                        <HelperText>{T._('Checked by default when the form loads.', 'Admin')}</HelperText>
                    </FormControl>
                </SeparatedBlock>
            )}

            {/* Min/Max length side by side (text / textArea) */}
            {(isText || isTextArea) &&
                editableItem?.type !== 'email' &&
                editableItem.hasOwnProperty('min_length') &&
                editableItem.hasOwnProperty('max_length') && (
                    <TwoColRow>
                        <FormControl label={T._('Min length', 'Admin')}>
                            <Input
                                id='min_length'
                                step='1'
                                type='number'
                                name='min_length'
                                className='form-control'
                                defaultValue={editableItem?.min_length}
                                onBlur={(e) => handleChange('min_length', e.currentTarget.value)}
                            />
                        </FormControl>
                        <FormControl label={T._('Max length', 'Admin')}>
                            <Input
                                id='max_length'
                                name='max_length'
                                step='1'
                                type='number'
                                className='form-control'
                                defaultValue={editableItem.max_length}
                                onBlur={(e) => handleChange('max_length', e.currentTarget.value)}
                            />
                        </FormControl>
                    </TwoColRow>
                )}

            {/* Min/Max value side by side (number fields) */}
            {isNumber && editableItem.hasOwnProperty('min_value') && editableItem.hasOwnProperty('max_value') && (
                <TwoColRow>
                    <FormControl label={T._('Min value', 'Admin')}>
                        <Input
                            id='min_value'
                            name='min_value'
                            type='number'
                            className='form-control'
                            defaultValue={editableItem.min_value}
                            onBlur={(e) => handleChange('min_value', e.currentTarget.value)}
                        />
                    </FormControl>
                    <FormControl label={T._('Max value', 'Admin')}>
                        <Input
                            id='max_value'
                            name='max_value'
                            step='1'
                            type='number'
                            className='form-control'
                            defaultValue={editableItem.max_value}
                            onBlur={(e) => handleChange('max_value', e.currentTarget.value)}
                        />
                    </FormControl>
                </TwoColRow>
            )}

            {/* Step (number fields) */}
            {isNumber && editableItem.hasOwnProperty('step') && (
                <FormControl label={T._('Step', 'Admin')}>
                    <Input
                        id='step'
                        name='step'
                        type='number'
                        className='form-control'
                        defaultValue={editableItem.step}
                        onBlur={(e) => handleChange('step', e.currentTarget.value)}
                    />
                    <HelperText>{T._('Increment between numeric values (e.g. 0.5).', 'Admin')}</HelperText>
                </FormControl>
            )}

            {/* Cols/Rows side by side (textarea) */}
            {isTextArea && editableItem.hasOwnProperty('cols') && editableItem.hasOwnProperty('rows') && (
                <TwoColRowWithCaption>
                    <TwoColRow>
                        <FormControl label={T._('Cols', 'Admin')}>
                            <Input
                                id='cols'
                                name='cols'
                                step='1'
                                type='number'
                                className='form-control'
                                defaultValue={30}
                                onBlur={(e) => handleChange('cols', e.currentTarget.value)}
                            />
                        </FormControl>
                        <FormControl label={T._('Rows', 'Admin')}>
                            <Input
                                id='rows'
                                name='rows'
                                step='1'
                                type='number'
                                className='form-control'
                                defaultValue={10}
                                onBlur={(e) => handleChange('rows', e.currentTarget.value)}
                            />
                        </FormControl>
                    </TwoColRow>
                    <HelperText>
                        {T._('Initial size of the text area: Cols is the width (in characters) and Rows is the height (in visible lines).', 'Admin')}
                    </HelperText>
                </TwoColRowWithCaption>
            )}
        </EditFormContainer>
    );
};

export default EditForm;
